// Section 7 (12-season resource history), Section 20 (fragility) and 24.11 (per-season series
// for charts and the steady-state check). Values are rounded to 1e-6 when written.
import { RESOURCES } from '../../core/constants';
import { round6 } from '../../core/math';
import { ageGroupOf, meanHappiness, peopleSorted, settlementTrust } from '../../core/queries';
import type { Metric, PhaseContext } from '../../core/types';
import { fragility } from '../shared/metrics';

/** A new rolling window: the last `cap - 1` values plus this one (one write per array). */
function rolled(list: readonly number[], value: number, cap: number): number[] {
  const out = list.slice(Math.max(0, list.length - cap + 1));
  out.push(round6(value));
  return out;
}

export function recordHistory(ctx: PhaseContext): void {
  const report = ctx.report.current;
  const cap = ctx.cfg.resources.historySeasons;
  for (const r of RESOURCES) {
    const f = report.flows[r];
    const h = ctx.state.resourceHistory[r];
    const amount = ctx.draft.stocks[r].amount;
    ctx.draft.resourceHistory[r] = {
      production: rolled(h.production, f.production, cap),
      consumption: rolled(h.consumption, f.consumption, cap),
      spoilage: rolled(h.spoilage, f.spoilage, cap),
      theft: rolled(h.theft, f.theft, cap),
      net: rolled(h.net, amount - report.stocksStart[r], cap),
    };
    report.stocksEnd[r] = amount;
  }
}

export function recordSeries(ctx: PhaseContext): void {
  const report = ctx.report.current;
  // People do not change in decay, so read them from the committed state; stocks from the draft.
  // Demand, services and the illness multiplier were set by earlier phases: read them from the
  // committed report, not through the draft (Section 2 budget).
  const committed = ctx.state.currentReport ?? report;
  const f = fragility(ctx, committed, ctx.draft.stocks.food.amount + ctx.draft.seedReserved);
  report.fragility = round6(f.value);
  report.fragilityFactors = f.factors;
  const people = peopleSorted(ctx.state);
  const groups = { child: 0, adult: 0, elder: 0 };
  let literate = 0;
  let sick = 0;
  for (const p of people) {
    groups[ageGroupOf(ctx.cfg, p.ageSeasons)] += 1;
    if (p.status.literate) literate++;
    if (p.status.sick !== null) sick++;
  }
  const s = ctx.draft.stocks;
  const values: Record<Metric, number> = {
    population: people.length,
    adults: groups.adult,
    children: groups.child,
    elders: groups.elder,
    food: s.food.amount,
    water: s.water.amount,
    wood: s.wood.amount,
    stone: s.stone.amount,
    tools: s.tools.amount,
    medicine: s.medicine.amount,
    cloth: s.cloth.amount,
    trust: settlementTrust(ctx.state),
    happiness: meanHappiness(ctx.state),
    fragility: f.value,
    gini: report.gini.food,
    giniBeds: report.gini.beds,
    literacy: people.length === 0 ? 0 : literate / people.length,
    births: report.population.births,
    deaths: report.population.deaths,
    sick,
  };
  for (const [metric, value] of Object.entries(values) as [Metric, number][])
    ctx.draft.series[metric].push(round6(value));
}
